import React, { useEffect, useState } from 'react';
import { RadioGroup } from '@payfit/midnight/dist/RadioGroup';
import { TextField } from '@payfit/midnight/dist/TextField';

import { Picto } from '../../components/Picto';
import { textBody, textSerifTitle1, textTitle3 } from '../../styles/typography';
import { classNames } from '../../utils/style';
import { useScreenSize } from '../../utils/utils';

import {
  getCumulativePayfitCosts,
  getCumulativePayfitGains,
  getExpenseSavingsPercentage,
  getPayrollManagementSavingsPercentage,
  getPerformanceManagement,
  getROI,
  getTaskSavings,
  getYearGainsDetailedResults,
} from './logic';
import { FullResultsCalculatorProps, PayfitPackageEnum } from './types';
import { Cta } from './Cta';

const optionsIntl: Intl.NumberFormatOptions = {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0,
  minimumFractionDigits: 0,
};

export const FullResultsCalculator = ({
  title,
  subtitle,
  cta,
}: FullResultsCalculatorProps) => {
  const screenSize = useScreenSize();
  const isDesktop =
    screenSize === undefined || !['xs', 'sm', 'md'].includes(screenSize);
  const payfitPackages = Object.keys(PayfitPackageEnum).filter((v) =>
    Number.isNaN(Number(v)),
  );
  const moduleExpenseOptions = [
    {
      label: 'OUI',
      value: 0,
    },
    {
      label: 'NON',
      value: 1,
    },
  ];
  const [numberEmployee, setNumberEmployee] = useState(10);
  const [payFitPackage, setPayFitPackage] = useState('Paie');
  const [moduleExpense, setModuleExpense] = useState(true);
  const [savings, setSavings] = useState('0€');

  const handleNumberEmployeeError = () =>
    !numberEmployee ||
      Number.isNaN(numberEmployee) ||
      numberEmployee < 1 ||
      numberEmployee > 164
      ? "Entrez un nombre d'employés entre 1 et 164"
      : null;

  useEffect(() => {
    setSavings(
      new Intl.NumberFormat().format(
        Math.ceil(
          getCumulativePayfitGains(
            payFitPackage,
            numberEmployee,
            moduleExpense,
          ),
        ),
      ),
    );
  }, [numberEmployee, payFitPackage, moduleExpense]);

  return (
    <section
      className={classNames(
        'FullResultsCalculator',
        'bg-blue-21 py-[40px] px-[16px] pb-[64px]',
        'md:p-[35px] md:pb-[104px]',
      )}
    >
      <div className="titles py-[64px] md:py-[80px] flex flex-col justify-center items-center gap-3">
        {title && (
          <h2
            className={classNames(
              textSerifTitle1,
              'text-neutral-301 text-center',
            )}
          >
            {title}
          </h2>
        )}
        {subtitle && (
          <p
            className={classNames(
              textTitle3,
              'font-light text-neutral-101 text-center w-auto md:w-[612px]',
            )}
          >
            {subtitle}
          </p>
        )}
      </div>
      <div className="inserts flex flex-col md:flex-row gap-5 justify-center">
        <div
          className={classNames(
            'insert-1',
            'bg-neutral-1 flex flex-col py-[35px] px-[15px] rounded-[18px] gap-5',
            'md:w-[500px] md:p-[35px]',
          )}
        >
          <h3 className="flex items-center gap-5">
            <div className="bg-blue-21 rounded-[6px]">
              <Picto name="employee_space" size="small" />
            </div>
            <span
              className={classNames(textTitle3, 'font-light text-neutral-301')}
            >
              Votre entreprise
            </span>
          </h3>

          <h4 className={classNames(textBody, 'font-light text-neutral-101')}>
            Combien avez-vous d&rsquo;employés ?
          </h4>
          <TextField
            hideLabel
            initialValue={'10'}
            label="Combien avez-vous d'employés ?"
            placeholder="10"
            contentType="number"
            addon="Employés"
            width={isDesktop ? 300 : 220}
            error={handleNumberEmployeeError() || ''}
            onChange={(v) => setNumberEmployee(parseInt(v, 10))}
          />
          <h3 className="flex items-center gap-5">
            <div className="bg-blue-21 rounded-[6px]">
              <Picto className="fillBlue" name="centralization" size="small" />
            </div>
            <span
              className={classNames(textTitle3, 'font-light text-neutral-301')}
            >
              Les offres PayFit
            </span>
          </h3>
          <h4 className={classNames(textBody, 'font-light text-neutral-101')}>
            Quelle formule souhaitez-vous ?{' '}
            {cta && (
              <Cta
                {...cta}
                className={classNames(
                  textBody,
                  'font-light text-blue-101 hover:underline',
                )}
              />
            )}
          </h4>
          <RadioGroup
            hideLabel
            type="badge"
            label="Quelle formule souhaitez-vous ?"
            initialValue={PayfitPackageEnum.Paie}
            options={payfitPackages.map((payfitPackage, i) => ({
              label: payfitPackage.toUpperCase(),
              value: i,
            }))}
            name="large"
            onChange={(value: any) => setPayFitPackage(payfitPackages[value])}
          />
          {payFitPackage !== 'Paie' && (
            <div>
              <h4
                className={classNames(textBody, ' font-light text-neutral-101')}
              >
                Voulez-vous le module notes de frais ?
              </h4>
              <RadioGroup
                hideLabel
                type="badge"
                label="Voulez-vous le module notes de frais ?"
                initialValue={moduleExpense ? 0 : 1}
                options={moduleExpenseOptions}
                name="large"
                onChange={(value: any) => setModuleExpense(!value)}
              />
            </div>
          )}
        </div>

        <div
          className={classNames(
            'insert-2',
            'bg-blue-301 flex flex-col py-[35px] px-[15px] rounded-[18px] gap-5',
            'md:p-[35px] md:w-[400px]',
          )}
        >
          <div className="titles">
            <p
              className={classNames(
                textBody,
                'font-light text-neutral-1 pb-[14px]',
              )}
            >
              Vos économies sur 3 ans
            </p>
            <div className={classNames(textSerifTitle1, 'text-neutral-21')}>
              {savings}€
            </div>
            <hr className="text-neutral-1" />
          </div>
          <div className="results">
            <p
              className={classNames(
                textBody,
                'font-light text-neutral-1 py-[14px]',
              )}
            >
              Économies
            </p>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between">
              <span>ROI</span>
              <span className="text-neutral-21 font-bold">
                {getROI(
                  getCumulativePayfitCosts(
                    payFitPackage,
                    numberEmployee,
                    moduleExpense,
                  ),
                  getCumulativePayfitGains(
                    payFitPackage,
                    numberEmployee,
                    moduleExpense,
                  ),
                )}
                %
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between">
              <span>Gestion de la paie</span>
              <span className="text-neutral-21 font-bold">
                {new Intl.NumberFormat('fr-FR', optionsIntl).format(
                  Math.ceil(
                    getYearGainsDetailedResults(
                      payFitPackage,
                      numberEmployee,
                      moduleExpense,
                    ).payrollProcessEfficiencyGains,
                  ),
                )}
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Notes de frais</span>
              <span className="text-neutral-21 font-bold">
                {getYearGainsDetailedResults(
                  payFitPackage,
                  numberEmployee,
                  moduleExpense,
                ).expenseManagementEfficiencyGain === 0
                  ? 'N/A'
                  : new Intl.NumberFormat('fr-FR', optionsIntl).format(
                    Math.ceil(
                      getYearGainsDetailedResults(
                        payFitPackage,
                        numberEmployee,
                        moduleExpense,
                      ).expenseManagementEfficiencyGain,
                    ),
                  )}
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Gestion des entretiens</span>
              <span className="text-neutral-21 font-bold">
                {getYearGainsDetailedResults(
                  payFitPackage,
                  numberEmployee,
                  moduleExpense,
                ).timeSavingsOnPerformanceManagement === 0
                  ? 'N/A'
                  : new Intl.NumberFormat('fr-FR', optionsIntl).format(
                    Math.ceil(
                      getYearGainsDetailedResults(
                        payFitPackage,
                        numberEmployee,
                        moduleExpense,
                      ).timeSavingsOnPerformanceManagement,
                    ),
                  )}
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Frais de comptabilité</span>
              <span className="text-neutral-21 font-bold">
                {new Intl.NumberFormat('fr-FR', optionsIntl).format(
                  Math.ceil(
                    getYearGainsDetailedResults(
                      payFitPackage,
                      numberEmployee,
                      moduleExpense,
                    ).costAvoidance,
                  ),
                )}
              </span>
            </div>
            <p
              className={classNames(
                textBody,
                'font-light text-neutral-1 py-[14px]',
              )}
            >
              Gains de temps
            </p>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Gestion de la paie</span>
              <span className="text-neutral-21 font-bold">
                {getPayrollManagementSavingsPercentage(payFitPackage)}
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Notes de frais</span>
              <span className="text-neutral-21 font-bold">
                {' '}
                {getExpenseSavingsPercentage(payFitPackage, moduleExpense)}
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Gestion des entretiens</span>
              <span className="text-neutral-21 font-bold">
                {getPerformanceManagement(payFitPackage, numberEmployee)}
              </span>
            </div>
            <div className="full-width font-light text-blue-51 flex flex-row justify-between align-center">
              <span>Onboarding & Offboarding</span>
              <span className="text-neutral-21 font-bold">
                {getTaskSavings(payFitPackage, numberEmployee)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
